import { Box, Link } from '@mui/material'
import { CellEditingStoppedEvent, ICellRendererParams } from 'ag-grid-community'
import { ComponentProps } from 'react'
import { Link as RouterLink } from 'react-router'
import { useMutation, useQuery } from 'urql'
import useEventStream from '../contexts/EventStream/useEventStream'
import { graphql } from '../gql'
import { UpdateRfqInput } from '../gql/graphql'
import { ListRfqsFilterInput } from '../gql/schema-types'
import { serializeDates } from '../utils/serialize-dates'
import DateDisplay from './DateDisplay'
import { ThemedAgGrid } from './ThemedAgGrid'

const listQuery = graphql(/* GraphQL */ `
  query RfqsList($filters: ListRfqsFilterInput) {
    Rfqs(filters: $filters) {
      id
      dateReceived
      client {
        id
        name
      }
      vendor {
        id
        name
      }
      project {
        id
        name
      }
    }
  }
`)

const updateRfqMutation = graphql(/* GraphQL */ `
  mutation UpdateRfq($id: Int!, $input: UpdateRfqInput!) {
    UpdateRfq(id: $id, input: $input) {
      id
    }
  }
`)

export default function RfqsList(props: {
  boxProps?: ComponentProps<typeof Box>
  gridProps?: ComponentProps<typeof ThemedAgGrid>
  filters?: ListRfqsFilterInput
}) {
  const [result, refetch] = useQuery({
    query: listQuery,
    requestPolicy: 'network-only',
    variables: { filters: props.filters },
  })
  const { data, fetching, error } = result

  const [updateResult, updateRfq] = useMutation(updateRfqMutation)

  const handleCellEditingStopped = async (e: CellEditingStoppedEvent) => {
    const { data, colDef } = e
    if (!data) return
    const id = data.id
    const field = colDef.field
    if (!field) return

    const input = { [field]: e.newValue } as UpdateRfqInput

    console.log('Updating rfq:', id, field, e.newValue)
    await updateRfq({ id, input })
  }

  useEventStream({
    onMessage: (message) => {
      if (message?.event?.startsWith('rfq.')) {
        setTimeout(() => refetch({ requestPolicy: 'network-only' }), 100)
      }
    },
  })

  if (fetching) return <p>Loading...</p>
  if (error) return <p>Oh no... {error.message}</p>
  if (!data || !data.Rfqs || !data.Rfqs.length) {
    return <p>No Rfqs</p>
  }

  const rowData = data.Rfqs.map(serializeDates)

  type TData = (typeof rowData)[0]

  return (
    <Box flex="1" {...(props.boxProps || {})}>
      <ThemedAgGrid
        rowData={rowData}
        {...(props.gridProps || {})}
        gridOptions={{
          autoSizeStrategy: {
            type: 'fitGridWidth',
            columnLimits: [
              {
                colId: 'id',
                minWidth: 60,
                maxWidth: 80,
              },
              {
                colId: 'actions',
                minWidth: 90,
                maxWidth: 90,
              },
            ],
          },
        }}
        onCellEditingStopped={handleCellEditingStopped}
        columnDefs={[
          {
            field: 'id',
            editable: false,
            cellRenderer: (d: ICellRendererParams<TData>) => {
              return (
                <Link component={RouterLink} to={`/order/${d.data!.id}`}>
                  Details
                </Link>
              )
            },
          },
          {
            field: 'vendor.name',
            headerName: 'Vendor',
            filter: true,
            editable: false,
          },
          {
            field: 'client.name',
            headerName: 'Client',
            filter: true,
            editable: false,
          },
          {
            field: 'project.name',
            headerName: 'Project',
            filter: true,
            editable: false,
            cellRenderer: (d: ICellRendererParams<TData>) => {
              return (
                <Link component={RouterLink} to={`/projects/${d.data!.project.id}`}>
                  {d.data!.project.name}
                </Link>
              )
            },
          },
          {
            field: 'dateReceived',
            filter: true,
            editable: true,
            cellDataType: 'date',
            cellRenderer: (d: ICellRendererParams<TData>) => {
              return <DateDisplay date={d.data!.dateReceived} />
            },
          },
        ]}
      />
    </Box>
  )
}
